import { NAMED_RESIDENTS } from "./content.ts";
import type {
  NamedResidentDefinition,
  ResidentPresentation,
  ResidentScheduleEntry,
} from "./types.ts";

export function scheduleEntryAt(
  resident: Readonly<NamedResidentDefinition>,
  hour: number,
): ResidentScheduleEntry {
  const normalized = ((hour % 24) + 24) % 24;
  return resident.schedule.find(
    (entry) => normalized >= entry.startHour && normalized < entry.endHour,
  ) ?? resident.schedule[0]!;
}

export function namedResidentPresentations(
  hour: number,
): readonly ResidentPresentation[] {
  return NAMED_RESIDENTS.map((resident) => {
    const entry = scheduleEntryAt(resident, hour);
    return {
      id: resident.id,
      name: resident.name,
      role: resident.role,
      position: { ...resident.anchors[entry.anchor] },
      yawRadians: 0,
      elderVariant: resident.elderVariant,
      clothing: resident.clothing,
      appearance: resident.appearance,
      activity: entry.activity,
      named: true,
    };
  });
}

export function nearbyResidentPresentations(
  presentations: readonly Readonly<ResidentPresentation>[],
  focus: Readonly<{ x: number; z: number }>,
  radiusM: number,
): readonly ResidentPresentation[] {
  return presentations.filter((resident) =>
    Math.hypot(
      resident.position.x - focus.x,
      resident.position.z - focus.z,
    ) <= radiusM
  );
}

/**
 * Retains already-presented named residents until they clear a wider despawn
 * radius. A retained resident also keeps its loaded schedule anchor, so a
 * clock boundary cannot teleport an on-screen character to another anchor.
 */
export function stableNearbyResidentPresentations(
  scheduled: readonly Readonly<ResidentPresentation>[],
  retained: readonly Readonly<ResidentPresentation>[],
  focus: Readonly<{ x: number; z: number }>,
  spawnRadiusM: number,
  despawnRadiusM: number,
): readonly ResidentPresentation[] {
  if (despawnRadiusM < spawnRadiusM) {
    throw new RangeError("Resident despawn radius must cover spawn radius");
  }
  const scheduledIds = new Set(scheduled.map((resident) => resident.id));
  const retainedById = new Map(
    retained
      .filter((resident) =>
        scheduledIds.has(resident.id) &&
        Math.hypot(
            resident.position.x - focus.x,
            resident.position.z - focus.z,
          ) <= despawnRadiusM
      )
      .map((resident) => [resident.id, resident]),
  );
  return scheduled.flatMap((resident) => {
    const previous = retainedById.get(resident.id);
    if (previous !== undefined) {
      return [previous];
    }
    return Math.hypot(
        resident.position.x - focus.x,
        resident.position.z - focus.z,
      ) <= spawnRadiusM
      ? [resident]
      : [];
  });
}
